import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus,
  Search,
  Shield,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  Calendar,
  Building2,
  X,
  Clock,
  TrendingUp,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const liabilityTypes = [
  { id: 'workers_comp', label: "Worker's Comp", color: 'bg-blue-100 text-blue-700' },
  { id: 'general_liability', label: 'General Liability', color: 'bg-green-100 text-green-700' },
  { id: 'auto_liability', label: 'Auto Liability', color: 'bg-purple-100 text-purple-700' },
  { id: 'umbrella', label: 'Umbrella', color: 'bg-orange-100 text-orange-700' },
  { id: 'bond', label: 'Bond', color: 'bg-yellow-100 text-yellow-700' },
  { id: 'other', label: 'Other', color: 'bg-gray-100 text-gray-600' }
];

const frequencies = [
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'quarterly', label: 'Quarterly' },
  { id: 'annually', label: 'Annually' },
  { id: 'one_time', label: 'One-Time' }
];

export const Liabilities = () => {
  const { api, isCOO } = useAuth();
  const [liabilities, setLiabilities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedLiability, setSelectedLiability] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (isCOO) fetchData();
  }, [isCOO]);

  const fetchData = async () => {
    try {
      const [liabilitiesRes, summaryRes] = await Promise.all([
        api.get('/liabilities/'),
        api.get('/liabilities/summary')
      ]);
      setLiabilities(liabilitiesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching liabilities:', error);
      toast.error('Failed to load liabilities');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLiability = async (data) => {
    try {
      await api.post('/liabilities/', data);
      toast.success('Liability added successfully');
      fetchData();
      setShowAddModal(false);
    } catch (error) {
      toast.error('Failed to add liability');
    }
  };

  const handleRecordPayment = async (data) => {
    try {
      await api.post('/liabilities/payments', data);
      toast.success('Payment recorded');
      fetchData();
      setShowPaymentModal(false);
      setSelectedLiability(null);
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const filteredLiabilities = liabilities.filter(l => {
    const matchesSearch = l.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          l.provider.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !filterType || l.liability_type === filterType;
    return matchesSearch && matchesType;
  });

  if (!isCOO) {
    return <Navigate to="/portal" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const getTypeInfo = (type) => liabilityTypes.find(t => t.id === type) || liabilityTypes[5];

  return (
    <div className="space-y-6" data-testid="liabilities-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl">Liabilities & Insurance</h1>
          <p className="text-gray-500 mt-1">Manage insurance and recurring liabilities</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 bg-[#C6A87C] text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
          data-testid="add-liability-btn"
        >
          <Plus className="w-4 h-4" />
          Add Liability
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-gray-100 p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 flex items-center justify-center">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Total Policies</span>
            </div>
            <p className="font-heading text-2xl">{summary.total_liabilities}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-gray-100 p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Monthly Cost</span>
            </div>
            <p className="font-heading text-2xl text-green-600">${summary.monthly_average?.toLocaleString()}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white border border-gray-100 p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Annual Cost</span>
            </div>
            <p className="font-heading text-2xl text-purple-600">${summary.annualized_cost?.toLocaleString()}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white border border-gray-100 p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-orange-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">Due Soon</span>
            </div>
            <p className="font-heading text-2xl text-orange-600">{summary.upcoming_payments?.length || 0}</p>
          </motion.div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-200">
        {['overview', 'upcoming'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-3 px-2 text-sm font-medium capitalize transition-colors ${
              activeTab === tab
                ? 'text-[#C6A87C] border-b-2 border-[#C6A87C]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'upcoming' ? 'Upcoming Payments' : tab}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <>
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search liabilities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
            >
              <option value="">All Types</option>
              {liabilityTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Liabilities List */}
          <div className="space-y-4">
            {filteredLiabilities.map((liability) => {
              const typeInfo = getTypeInfo(liability.liability_type);
              return (
                <motion.div
                  key={liability.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white border border-gray-100 p-6 hover:border-[#C6A87C]/50 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gray-100 flex items-center justify-center">
                        <Shield className="w-6 h-6 text-gray-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{liability.name}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${typeInfo.color}`}>
                            {typeInfo.label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">{liability.provider}</p>
                        {liability.policy_number && (
                          <p className="text-xs text-gray-400">Policy: {liability.policy_number}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-2xl font-bold">${liability.amount.toLocaleString()}</p>
                        <p className="text-xs text-gray-500 capitalize">{liability.frequency.replace('_', ' ')}</p>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedLiability(liability);
                          setShowPaymentModal(true);
                        }}
                        className="px-4 py-2 bg-[#C6A87C] text-white text-sm font-medium hover:bg-[#B09265] transition-colors"
                      >
                        Record Payment
                      </button>
                    </div>
                  </div>
                  {liability.next_payment_date && (
                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      Next payment: {new Date(liability.next_payment_date).toLocaleDateString()}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </>
      )}

      {activeTab === 'upcoming' && summary?.upcoming_payments && (
        <div className="space-y-4">
          {summary.upcoming_payments.length > 0 ? (
            summary.upcoming_payments.map((payment, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white border border-gray-100 p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-orange-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium">{payment.name}</p>
                    <p className="text-sm text-gray-500">
                      Due: {new Date(payment.due_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <p className="text-xl font-bold">${payment.amount.toLocaleString()}</p>
              </motion.div>
            ))
          ) : (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="text-gray-600">No upcoming payments in the next 30 days</p>
            </div>
          )}
        </div>
      )}

      {/* Add Liability Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddLiabilityModal
            onClose={() => setShowAddModal(false)}
            onSubmit={handleAddLiability}
          />
        )}
      </AnimatePresence>

      {/* Record Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && selectedLiability && (
          <RecordPaymentModal
            liability={selectedLiability}
            onClose={() => {
              setShowPaymentModal(false);
              setSelectedLiability(null);
            }}
            onSubmit={handleRecordPayment}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Add Liability Modal Component
const AddLiabilityModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    liability_type: 'workers_comp',
    frequency: 'monthly',
    amount: '',
    provider: '',
    policy_number: '',
    start_date: new Date().toISOString().split('T')[0],
    next_payment_date: '',
    notes: '',
    auto_pay: false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      amount: parseFloat(formData.amount)
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="font-heading text-xl">Add Liability</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              placeholder="e.g., Worker's Compensation Insurance"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <select
                value={formData.liability_type}
                onChange={(e) => setFormData({ ...formData, liability_type: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              >
                {liabilityTypes.map((type) => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Frequency</label>
              <select
                value={formData.frequency}
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              >
                {frequencies.map((freq) => (
                  <option key={freq.id} value={freq.id}>{freq.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Amount ($)</label>
            <input
              type="number"
              required
              min="0"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Provider</label>
            <input
              type="text"
              required
              value={formData.provider}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              placeholder="Insurance company name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Policy Number (Optional)</label>
            <input
              type="text"
              value={formData.policy_number}
              onChange={(e) => setFormData({ ...formData, policy_number: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Start Date</label>
              <input
                type="date"
                required
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Next Payment</label>
              <input
                type="date"
                value={formData.next_payment_date}
                onChange={(e) => setFormData({ ...formData, next_payment_date: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none resize-none"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="auto_pay"
              checked={formData.auto_pay}
              onChange={(e) => setFormData({ ...formData, auto_pay: e.target.checked })}
              className="w-4 h-4 text-[#C6A87C] border-gray-300 rounded focus:ring-[#C6A87C]"
            />
            <label htmlFor="auto_pay" className="text-sm">Auto-pay enabled</label>
          </div>

          <button
            type="submit"
            className="w-full bg-[#C6A87C] text-white py-3 font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
          >
            Add Liability
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Record Payment Modal Component
const RecordPaymentModal = ({ liability, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    liability_id: liability.id,
    amount: liability.amount,
    payment_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      amount: parseFloat(formData.amount)
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-heading text-xl">Record Payment</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-500">Liability</p>
            <p className="font-semibold">{liability.name}</p>
            <p className="text-sm text-gray-500">{liability.provider}</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Amount ($)</label>
            <input
              type="number"
              required
              min="0"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Payment Date</label>
            <input
              type="date"
              required
              value={formData.payment_date}
              onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none resize-none"
              placeholder="Optional notes"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-[#C6A87C] text-white py-3 font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
          >
            Record Payment
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default Liabilities;
